import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

// store
import { useStoreValue, storeSet } from '../../store';

// components
import StudioTimeline from './components/StudioTimeline';
import RecSessionTimeline from './components/RecSessionTimeline';
import TimelineControlsWidget from '../../components/TimelineControlsWidget';
import MyStudioLeftPanel from './components/MyStudioLeftPanel';
import PinIcon from '../../icons/PinIcon';
import ButtonIconRadioFlat from '../../components/ButtonWrapper/components/ButtonIconRadioFlat';
import WizardIcon from '../../icons/WizardIcon';
import RollingArrowsIcon from '../../icons/RollingArrowsIcon';
import PatchIcon from '../../icons/PatchIcon';
import PenAiIcon from '../../icons/PenAiIcon';
import PinAiIcon from '../../icons/PinAiIcon';
import MoodAiIcon from '../../icons/MoodAiIcon';
import CommCenterMyStudio from './components/CommCenterMyStudio';
import ModalStemSplitter from '../../components/ModalStemSplitter';
import useCacheProjectAudio from '../../hooks/useCacheProjectAudio';

const ProjectMyStudio = () => {
    const { projectId } = useParams();
    const selectedVideo = useStoreValue('selectedVideo');
    const myStudioLeftPanel = useStoreValue('myStudioLeftPanel');
    const stemSplitterSelection = useStoreValue('stemSplitterSelection');
    const generateInstrumentSelection = useStoreValue('generateInstrumentSelection');
    const lastProjectId = useRef(null);
    const prevSelectedVideoRef = useRef(null);

    // Button group state - only one can be selected at a time
    const [aiEditTool, setAiEditTool] = useState(null);

    const handleButtonToggle = useCallback((value) => {
        setAiEditTool(prev => {
            const next = prev === value ? null : value;
            if (value === 'stemSplitter') {
                storeSet('timelineEditingMode', next === 'stemSplitter' ? 'stemSplitter' : '');
            }
            if (value === 'generateInstrument') {
                storeSet('timelineEditingMode', next === 'generateInstrument' ? 'generateInstrument' : '');
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (lastProjectId.current !== projectId) {
            if (lastProjectId.current) {
                storeSet('cleanupVideoCache', lastProjectId.current, null);
            }
            lastProjectId.current = projectId;
            storeSet('refetchCueSheet', projectId);
        }
    }, [projectId]);

    useCacheProjectAudio(projectId);

    // Handle selectedVideo changes - cache selected video
    useEffect(() => {
        if (prevSelectedVideoRef.current !== selectedVideo) {
            if (prevSelectedVideoRef.current !== null && prevSelectedVideoRef.current !== 0) {
                storeSet('cleanupVideoCache', projectId, prevSelectedVideoRef.current);
            }
            if (selectedVideo && selectedVideo !== 0) {
                storeSet('cacheSelectedVideo', projectId);
            }
            prevSelectedVideoRef.current = selectedVideo;
        }
    }, [selectedVideo, projectId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (projectId) {
                storeSet('cleanupAudioCache', projectId);
            }
        };
    }, [projectId]);

    return (
        <>
            <div className="appDashboard appMyStudio">
                <div className="appDashboard__workbench timelineEditorAuxWorkbench">
                    <div className="appWorkbenchPanel">
                        <MyStudioLeftPanel/>
                    </div>
                </div>
                <div className="appDashboard__workbench timelineEditorWorkbench">
                    {myStudioLeftPanel !== 'rec' ? <StudioTimeline key={projectId} /> : <RecSessionTimeline />}
                </div>
                <CommCenterMyStudio/>
            </div>
            <div className="appDashboard__widgets appMyStudio__widgets">
                <div className="appWorkbenchPanel">
                    <div>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('stemSplitter')}
                            value="stemSplitter"
                            isSquare={true}
                            checked={aiEditTool === 'stemSplitter'}>
                            <PinIcon/>
                        </ButtonIconRadioFlat>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('generateInstrument')}
                            value="generateInstrument"
                            isSquare={true}
                            checked={aiEditTool === 'generateInstrument'}
                            disabled={myStudioLeftPanel !== 'rec'}>
                            <WizardIcon/>
                        </ButtonIconRadioFlat>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('rolling')}
                            value="rolling"
                            isSquare={true}
                            checked={aiEditTool === 'rolling'}>
                            <RollingArrowsIcon/>
                        </ButtonIconRadioFlat>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('patch')}
                            value="patch"
                            isSquare={true}
                            checked={aiEditTool === 'patch'}>
                            <PatchIcon/>
                        </ButtonIconRadioFlat>
                    </div>

                    <TimelineControlsWidget />

                    <div>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('penai')}
                            value="penai"
                            isSquare={true}
                            checked={aiEditTool === 'penai'}>
                            <PenAiIcon/>
                        </ButtonIconRadioFlat>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('pinai')}
                            value="pinai"
                            isSquare={true}
                            checked={aiEditTool === 'pinai'}>
                            <PinAiIcon/>
                        </ButtonIconRadioFlat>
                        <ButtonIconRadioFlat
                            onChange={() => handleButtonToggle('moodai')}
                            value="moodai"
                            isSquare={true}
                            checked={aiEditTool === 'moodai'}>
                            <MoodAiIcon/>
                        </ButtonIconRadioFlat>
                    </div>
                </div>
            </div>
            <ModalStemSplitter
                visible={!!stemSplitterSelection}
                selection={stemSplitterSelection}
                onHide={() => {
                    storeSet('stemSplitterSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
                onSubmit={(data) => {
                    console.log('Stem Splitter submit:', data);
                    storeSet('stemSplitterSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
            />
            <ModalStemSplitter
                title="Genera strumento"
                visible={!!generateInstrumentSelection}
                selection={generateInstrumentSelection}
                onHide={() => {
                    storeSet('generateInstrumentSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
                onSubmit={(data) => {
                    console.log('Generate Instrument submit:', data);
                    storeSet('generateInstrumentSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
            />
        </>
    )
}

export default ProjectMyStudio;